import { NotificationIcon } from "@hoylu/notifications";
import React, { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import { DashboardOption } from "@/enums/DashboardOption.enum";
import {
  enableInAppProjectCreation,
  selectCanUseProjects,
  showNotificationsUI as selectShowNotificationsUI,
} from "../../state/config/config.selector";
import { AppMode } from "../../state/mode/mode.reducer";
import type { AppState } from "../../state/root.reducer";
import { selectUserIsLoggedIn } from "../../state/user/user.selector";
import {
  getAllProjects,
  getSelectedProjectColor,
  selectDashboardSelectedProject,
} from "../../state/workspaces/projects.selector";
import type { Label } from "../../state/workspaces/types";
import * as workspacesActions from "../../state/workspaces/workspaces.actions";
import {
  isNotificationCenterOpen as selectIsNotificationCenterOpen,
  selectWorkspacesAllLabels,
  selectWorkspacesSearchLabels,
  selectWorkspacesSearchTerm,
} from "../../state/workspaces/workspaces.selector";
import { hexToRgba } from "../../utils/hexToRgba";
import { useI18n } from "../../utils/hooks/use.i18n";
import { FilterByWorkspaceName } from "../FilterByWorkspaceName";
import { FilterByWorkspaceTag } from "../FilterByWorkspaceTag";
import HoyluLogo from "../HoyluLogo";
import { HelpMenu } from "../help/help.menu";
import ProjectNavigationDropdown from "../project/ProjectNavigationDropdown";
import { FlatRippleSpinner } from "../RippleSpinner";
import UserProfileWidget from "../UserProfileWidget";
import Styles from "./Header.module.css";
import HeaderButtons from "./HeaderButtons";
import { ProjectNavigationInformation } from "./ProjectNavigationInformation";

const selectAllProjectsSorted = createSelector(
  [getAllProjects],
  (allProjects) =>
    (allProjects || [])
      /* Inactive projects last */
      .sort((a, b) => {
        const statusA = a.status === "Inactive" ? 1 : 0;
        const statusB = b.status === "Inactive" ? 1 : 0;

        return statusA - statusB;
      }),
);

export const Header = () => {
  const dispatch = useDispatch();

  const t = useI18n("NOTIFICATIONS.");
  const tSharedWorkspace = useI18n("SHARED_WORKSPACE.");
  const tInviteUsers = useI18n("INVITE_USERS.");
  const tHelpMenu = useI18n("HELP_MENU.");

  const allLabels = useSelector(selectWorkspacesAllLabels);
  const searchTerm = useSelector(selectWorkspacesSearchTerm);
  const searchLabels = useSelector(selectWorkspacesSearchLabels);

  const allowCreatingProject = useSelector(enableInAppProjectCreation);
  const projectThemeColor = useSelector(getSelectedProjectColor);
  const isLoggedIn = useSelector(selectUserIsLoggedIn);
  const isLoading = useSelector((state: AppState) => {
    return (
      [AppMode.LOADING_DASHBOARD, AppMode.LOADING_WORKSPACE].includes(
        state.mode,
      ) || state.context.workspaces.isLoadingSelectedProjectWorkspaces
    );
  });
  const isNotificationCenterOpen = useSelector(selectIsNotificationCenterOpen);
  const selectedProject = useSelector(selectDashboardSelectedProject);
  const showNotificationsUI = useSelector(selectShowNotificationsUI);
  const showProjects = useSelector(selectCanUseProjects);
  const showSearch = useSelector((state: AppState) => {
    return ![
      AppMode.NOT_VERIFIED_POLICY_RESTRICTED,
      AppMode.BETA_NOT_ALLOWED,
    ].includes(state.mode);
  });
  const sortedProjects = useSelector(selectAllProjectsSorted);

  const backgroundColor = useMemo(
    () =>
      projectThemeColor
        ? hexToRgba(projectThemeColor)
        : "var(--gradient-from-color-theme)",
    [projectThemeColor],
  );

  const handleSearchClick = () => {
    dispatch(
      workspacesActions.activateDashboardOption({
        optionType: DashboardOption.OPEN,
      }),
    );
  };

  const handleLabelsChange = (selectedLabels: Label[]) => {
    dispatch(workspacesActions.setSearchLabels(selectedLabels));
  };

  const toggleNotificationCenterVisibility = () => {
    if (isNotificationCenterOpen) {
      dispatch(workspacesActions.cancelDashboardOption());
    } else {
      dispatch(
        workspacesActions.activateDashboardOption({
          optionType: DashboardOption.NOTIFICATION_CENTER,
          dialogPosition: { top: 130, right: 30 },
        }),
      );
    }
  };

  return (
    <div className={Styles.header}>
      <div className={Styles.headerDivider} style={{ backgroundColor }} />
      <div className={Styles.headerContent}>
        <div className={Styles.headerBody}>
          <div className={Styles.logoContainer}>
            <HoyluLogo />
            {isLoggedIn && !selectedProject && (
              <HeaderButtons
                handleSearchClick={handleSearchClick}
                localizationDict={{
                  open: {
                    tooltip: tSharedWorkspace("OPEN_SHARED_WORKSPACE"),
                    text: tSharedWorkspace("OPEN"),
                  },
                  invite: {
                    tooltip: tInviteUsers("INVITE_USER"),
                    text: tInviteUsers("INVITE"),
                  },
                }}
                showSearch={showSearch}
              />
            )}
          </div>
          <div className={Styles.projectNavigationContainer}>
            <ProjectNavigationDropdown
              allowCreatingProject={allowCreatingProject}
              allProjects={sortedProjects}
              selectedProject={selectedProject}
              showProjects={showProjects}
            />
            <ProjectNavigationInformation />
          </div>
          <div className={Styles.filterWorkspaceContainer}>
            <FilterByWorkspaceName searchTerm={searchTerm} />
            <FilterByWorkspaceTag
              allLabels={allLabels}
              handleLabelsChange={handleLabelsChange}
              selectedLabels={searchLabels}
            />
            {showNotificationsUI && (
              <NotificationIcon
                onClick={toggleNotificationCenterVisibility}
                showValue={false}
                styles={{
                  color: "var(--theme-text)",
                  lineHeight: 1,
                  paddingRight: 0,
                }}
                title={t("TITLE")}
              />
            )}
            <HelpMenu localization={tHelpMenu} />
            <div className={Styles.userProfileWidgetContainer}>
              {isLoggedIn && <UserProfileWidget />}
            </div>
          </div>
        </div>
        {isLoading && <FlatRippleSpinner />}
      </div>
    </div>
  );
};

export default Header;
